import React from "react";
import LegalPage from "../../components/Kids/LegalPage";
import Seo from "../../components/Seo";
import SEO from "../../seo/pages.json";

export default function KidsPrivacyPolicy() {
  return (
    <LegalPage title="Privacy Policy" updated="July 25, 2026">
      <Seo
        {...SEO["/kids/privacy"]}
        path="/kids/privacy"
      />

      <p>
        Cabil Kids ("Cabil Kids", "we", "us") is operated by Cabil. This policy explains what
        information we collect through the Cabil Kids website and mobile app, how we use it, and
        the choices parents have — including how to delete an account. Cabil Kids is built for
        children, so a parent creates and controls every account; a child never signs up on
        their own.
      </p>

      <h2>Information we collect</h2>
      <p><strong>From the parent, at signup:</strong> name, email address, phone number, and
        optionally country, city, and school. Email is verified before the account can log in.</p>
      <p><strong>About each child, added by the parent:</strong> a first name or nickname, grade
        level, an optional date of birth, an avatar, and an optional PIN the parent sets so the
        child can sign in on their own. We do not require a child's email address, phone number,
        or physical address.</p>
      <p><strong>Gameplay data:</strong> which games a child plays, scores, XP, coins, streaks,
        levels, and time spent — used to show progress to the parent and adapt difficulty.</p>
      <p><strong>Watch Zone:</strong> the YouTube videos a parent chooses to approve, and how many
        reward minutes a child has earned and spent — Cabil Kids never lets a child browse YouTube
        directly or see comments/recommendations.</p>
      <p><strong>AI features:</strong> if a family uses Cabi (the AI study buddy) or the AI game
        generator, the messages or prompts sent are processed by our AI provider (Anthropic's
        Claude API) to generate a response; we don't use this content to train third-party models.</p>
      <p><strong>Technical data:</strong> device/browser type, IP address, and basic usage
        analytics (only if a parent's region has analytics enabled), used to keep the app secure
        and working correctly.</p>

      <h2>What we don't do</h2>
      <ul>
        <li>No advertising, and no selling or renting family data to advertisers or data brokers.</li>
        <li>No open chat between children, and no chat with strangers.</li>
        <li>No in-app purchases a child can make — subscriptions are managed only from the parent's own account.</li>
      </ul>

      <h2>How we use information</h2>
      <p>
        To run the app (accounts, gameplay, progress tracking), to keep it secure (fraud and abuse
        prevention, rate limiting), to communicate with the parent (email verification, support,
        service updates), and to improve the product. We do not use a child's data for
        behavioral advertising.
      </p>

      <h2>Who we share information with</h2>
      <p>
        Only service providers that help us run Cabil Kids — hosting/infrastructure, our AI
        provider (Anthropic) for AI features, email delivery for verification and support, and
        (when a parent completes a subscription) our payment processor. These providers only
        receive what they need to perform their function and aren't permitted to use it for
        their own purposes.
      </p>

      <h2>Your choices</h2>
      <ul>
        <li>Edit your profile, phone, city, school, or password any time from Settings.</li>
        <li>Edit or remove a child's profile any time.</li>
        <li>Delete your account from Settings (web) or the Profile screen (app) — this immediately
          signs the account out everywhere and deactivates it. To request full, permanent erasure
          of historical data rather than deactivation, message us on WhatsApp (see Contact below)
          or email support.</li>
      </ul>

      <h2>Children's privacy</h2>
      <p>
        Cabil Kids is designed for children but every account is created and controlled by a
        parent, who verifies their own email before any child profile can be added. We collect
        the minimum information needed for a child to use the app — see "Information we collect"
        above — and never require a child to provide their own contact details.
      </p>

      <h2>Security</h2>
      <p>
        Passwords are stored hashed, never in plain text. Sessions use time-limited access tokens;
        the mobile app additionally stores tokens in the device's secure keychain/keystore rather
        than plain storage. No system is perfectly secure, and we continue to invest in hardening
        the platform.
      </p>

      <h2>Changes to this policy</h2>
      <p>
        If this policy changes materially, we'll update the date above and, where appropriate,
        notify parents directly.
      </p>

      <h2>Contact</h2>
      <p>
        Questions, data requests, or account deletion requests: message us on{" "}
        <a href="https://wa.me/+923314622245" target="_blank" rel="noreferrer">WhatsApp</a>{" "}
        or through the <a href="/contact-us">Contact page</a>.
      </p>
    </LegalPage>
  );
}
